import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

interface Agent {
  id: string;
  name: string;
  color?: string;
  can_assign?: boolean;
}

interface Props {
  selectedIds: string[];
  endpoint: string; // '/api/assign/messages' | '/api/assign/issues' | '/api/assign/email-threads'
  currentUser: { can_assign?: boolean; role?: string } | null;
  onAssigned: () => void;
  size?: 'small' | 'normal';
}

export default function AssignButton({ selectedIds, endpoint, currentUser, onAssigned, size = 'normal' }: Props) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const canAssign = currentUser?.role === 'admin' || currentUser?.can_assign;

  useEffect(() => {
    if (open && agents.length === 0) {
      axios.get('/api/agents').then(r => setAgents(r.data)).catch(() => {});
    }
  }, [open, agents.length]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!canAssign || selectedIds.length === 0) return null;

  const assign = async (agentId: string | null) => {
    setLoading(true);
    try {
      await axios.post(endpoint, { thread_ids: selectedIds, assigned_to: agentId });
      onAssigned();
      setOpen(false);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const btnStyle: React.CSSProperties = size === 'small' ? {
    fontSize: 11, padding: '3px 8px', borderRadius: 20, border: '1px solid #d1d5db',
    background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer', whiteSpace: 'nowrap' as const
  } : {
    fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #bfdbfe',
    background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer', whiteSpace: 'nowrap' as const
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={() => setOpen(v => !v)} style={btnStyle} disabled={loading}>
        👤 Agent ({selectedIds.length})
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 9999,
          minWidth: 180, maxHeight: 280, overflowY: 'auto'
        }}>
          <div style={{ padding: '6px 10px', fontSize: 11, color: '#9ca3af', borderBottom: '1px solid #f3f4f6' }}>
            Przydziel do agenta
          </div>
          {agents.map(a => (
            <div key={a.id} onClick={() => assign(a.id)}
              style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 13, color: '#374151' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: a.color || '#9ca3af', flexShrink: 0 }} />
              {a.name}
            </div>
          ))}
          <div onClick={() => assign(null)}
            style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 13, color: '#6b7280', borderTop: '1px solid #f3f4f6' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#e5e7eb', flexShrink: 0 }} />
            Odepnij agenta
          </div>
        </div>
      )}
    </div>
  );
}
